import type { Site } from "./api/types";

const SITE_CHANGED_EVENT = "protopal:site-changed";
type SiteChangeSource = "mutation" | "observation";

// Keep independently mounted workspace and session consumers in sync after a mutation.
export function notifySiteChanged(source: SiteChangeSource = "mutation"): void {
  window.dispatchEvent(new CustomEvent(SITE_CHANGED_EVENT, { detail: source }));
}

export function subscribeToSiteChanges(listener: () => void, includeObservations = true): () => void {
  const handle = (event: Event) => {
    if (includeObservations || (event as CustomEvent<SiteChangeSource>).detail !== "observation") listener();
  };
  window.addEventListener(SITE_CHANGED_EVENT, handle);
  return () => window.removeEventListener(SITE_CHANGED_EVENT, handle);
}

const observedSites = new Map<string, string>();
let observedList: string | undefined;
const signature = (site: Site) => JSON.stringify([site.id, site.ownerUserId, site.subdomain, site.status,
  site.pendingBuildId, site.previewBuildId, site.liveBuildId, site.archivedAt]);

// Reads can settle a build on the server. Refresh session consumers when the
// canonical pointers change, but never trigger another site read from that read.
export function observeSite(site: Site): void {
  const next = signature(site);
  if (observedSites.get(site.id) === next) return;
  observedSites.set(site.id, next);
  notifySiteChanged("observation");
}

export function observeSiteList(sites: Site[]): void {
  const next = JSON.stringify(sites.map(signature).sort());
  sites.forEach(site => observedSites.set(site.id, signature(site)));
  if (next === observedList) return;
  observedList = next;
  notifySiteChanged("observation");
}
